import type { ReactNode } from 'react';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { VerticalStack, type VerticalStackProps } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Card, type CardProps } from '@interxfi/design-system/components/card/card';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY, Typography } from '@interxfi/design-system/components/typography';

type FormConfirmationGroupType = 'list' | 'node';

interface FormConfirmationGroupBase {
  type?: FormConfirmationGroupType;
  id: string;
  cardProps?: CardProps;
}

interface FormConfirmationGroupNode extends FormConfirmationGroupBase {
  type: 'node';
  node: ReactNode;
  title?: ReactNode;
}

export type FormConfirmationGroupListType = Omit<ReadonlyListGroup, 'changeItems' | 'titleGap' | 'titleTypography'>;

interface FormConfirmationGroupList extends FormConfirmationGroupBase {
  type?: 'list';
  title?: ReactNode;
  list: Omit<FormConfirmationGroupListType, 'id' | 'title'> | FormConfirmationGroupListType[];
  listGap?: VerticalStackProps['gap'];
}

export type FormConfirmationGroup = FormConfirmationGroupNode | FormConfirmationGroupList;

function FormConfirmationGroupRenderer(props: FormConfirmationGroup) {
  switch (props.type) {
    case 'node':
      return (
        <Card title={props.title} {...props.cardProps}>
          {props.node}
        </Card>
      );
    default:
      return (
        <Card title={props.title} {...props.cardProps}>
          {Array.isArray(props.list) ? (
            <AppReadonlyListStack gap={props.listGap ?? 3} groups={props.list} titleGap={3} titleTypography={DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY} />
          ) : (
            <AppReadonlyList titleGap={4} titleTypography={DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY} {...props.list} />
          )}
        </Card>
      );
  }
}

interface FormConfirmationDetailsProps extends Omit<VerticalStackProps, 'children' | 'title'> {
  title?: ReactNode;
  groups: FormConfirmationGroup[];
}

export function FormConfirmationDetails({ gap = 7, groups, title = 'Review:', ...rest }: FormConfirmationDetailsProps) {
  return (
    <VerticalStack gap={gap} {...rest}>
      {title ? (
        <Typography lineHeight="1.625rem" size="1.125rem" variant="heading" weight="bold">
          {title}
        </Typography>
      ) : null}

      {groups.map((group) => (
        <FormConfirmationGroupRenderer key={group.id} {...group} />
      ))}
    </VerticalStack>
  );
}
